/**
 * Live preview for the collector-split dialog: how much m² each half gets.
 *
 * This MIRRORS `portal-api/src/utils/collector-split.ts`. The server owns the split —
 * it recomputes everything and its numbers are the ones that land — so this exists
 * only so the dialog can show the consequence of a checkbox before you press save.
 * Keep the two in step: same length-share rule, same 2-decimal rounding, same
 * remainder-on-deel-1, same refusals. A drifting preview is a lie about money.
 */

export interface SplitPreviewKring {
  id: number;
  x_length: number;
}

export type SplitPreviewProblem =
  | 'NO_KRINGEN'
  | 'NOTHING_MOVED'
  | 'EVERYTHING_MOVED'
  | 'NO_M2'
  | 'NO_KRING_LENGTH'
  | 'HALF_ROUNDS_TO_ZERO';

export interface SplitPreview {
  /** null while the selection is not splittable — the reason is in `problem`. */
  m2Deel1: number | null;
  m2Deel2: number | null;
  lengthDeel1: number;
  lengthDeel2: number;
  countDeel1: number;
  countDeel2: number;
  problem: SplitPreviewProblem | null;
}

export function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Dutch one-liner for a preview problem, shown under the two columns. */
export const SPLIT_PROBLEM_TEXT: Record<SplitPreviewProblem, string> = {
  NO_KRINGEN: 'Deze collector heeft geen kringen om te splitsen.',
  NOTHING_MOVED: 'Kies minstens één kring voor deel 2.',
  EVERYTHING_MOVED: 'Er moet minstens één kring op deel 1 blijven.',
  NO_M2: 'Deze collector heeft geen m² — vul de m² in voor je splitst.',
  NO_KRING_LENGTH: 'Geen enkele kring heeft een lengte — vul de kringlengtes in, anders kan de m² niet verdeeld worden.',
  HALF_ROUNDS_TO_ZERO: 'Deze verdeling geeft 0 m² aan één van de twee delen.',
};

export function previewSplit(
  kringen: readonly SplitPreviewKring[],
  movedIds: ReadonlySet<number>,
  collectorM2: number,
): SplitPreview {
  let lengthDeel1 = 0;
  let lengthDeel2 = 0;
  let countDeel1 = 0;
  let countDeel2 = 0;
  for (const k of kringen) {
    const len = k.x_length || 0;
    if (movedIds.has(k.id)) {
      lengthDeel2 += len;
      countDeel2 += 1;
    } else {
      lengthDeel1 += len;
      countDeel1 += 1;
    }
  }

  const base = { lengthDeel1, lengthDeel2, countDeel1, countDeel2, m2Deel1: null, m2Deel2: null };

  if (kringen.length === 0) return { ...base, problem: 'NO_KRINGEN' };
  if (countDeel2 === 0) return { ...base, problem: 'NOTHING_MOVED' };
  if (countDeel1 === 0) return { ...base, problem: 'EVERYTHING_MOVED' };

  const m2 = round2(collectorM2 || 0);
  if (m2 <= 0) return { ...base, problem: 'NO_M2' };

  const totalLength = lengthDeel1 + lengthDeel2;
  if (totalLength <= 0) return { ...base, problem: 'NO_KRING_LENGTH' };

  const m2Deel2 = round2((lengthDeel2 / totalLength) * m2);
  const m2Deel1 = round2(m2 - m2Deel2);
  if (m2Deel2 <= 0 || m2Deel1 <= 0) return { ...base, problem: 'HALF_ROUNDS_TO_ZERO' };

  return { ...base, m2Deel1, m2Deel2, problem: null };
}

/** Prefilled names — mirrors `suggestSplitNames` on the API. */
export function suggestSplitNames(name: string): { sourceName: string; newName: string } {
  const trimmed = (name ?? '').trim();
  const already = trimmed.match(/^(.*?)[\s-]+deel\s+(\d+)$/i);
  if (already) {
    const base = already[1].trim();
    return { sourceName: trimmed, newName: `${base} deel ${Number(already[2]) + 1}` };
  }
  return { sourceName: `${trimmed} deel 1`, newName: `${trimmed} deel 2` };
}
